import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  setHighGrainEndDateTime,
  setHighGrainExecute,
  setHighGrainStartDateTime,
  setRegularStartEndDate,
} from 'store/sensorAnalysis.slice';
import { StyledButton, StyledTypography } from './SensorAnalysis.styles';
import { DateSelectorMui } from 'views/DriverBehavior/Inclinometer/DateSelectorMui';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { useTranslation } from 'react-i18next';

const MAX_DATE = dayjs().endOf('day');

const getEndDateTime = (start: Date) => {
  return dayjs(start).add(4, 'hour').toDate();
};

export const SensorAnalysisCustomDateComponent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { activeButton, startDate, endDate } = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters
  );

  const startDateTime = useAppSelector(
    (state) => state.sensorAnalysisReducer.highGrainFilters.startDateTime
  );

  const endDateTime = useAppSelector(
    (state) => state.sensorAnalysisReducer.highGrainFilters.endDateTime
  );

  const memoisedStartDate = useMemo(
    () => dayjs(startDate).toDate(),
    [startDate]
  );
  const memoisedEndDate = useMemo(() => dayjs(endDate).toDate(), [endDate]);

  const dataType = useAppSelector(
    (state) => state.sensorAnalysisReducer.dataType
  );

  const startDateTimeDayjs = useMemo(
    () => dayjs(startDateTime),
    [startDateTime]
  );

  const endDateTimeDayjs = useMemo(() => dayjs(endDateTime), [endDateTime]);

  const maxEndTime = useMemo(
    () => getEndDateTime(startDateTimeDayjs.toDate()),
    [startDateTimeDayjs]
  );

  const maxEndTimeDayjs = useMemo(() => dayjs(maxEndTime), [maxEndTime]);

  const startMaxDayjs = useMemo(() => dayjs(), []);

  const startMinDayjs = useMemo(() => dayjs('2010-01-01'), []);

  const [openEndDate, setOpenEndDate] = useState<boolean>(false);

  const handleFinalRegularDateChange = (range: [Date | null, Date | null]) => {
    const [startDate, endDate] = range;
    const startMs = startDate?.getTime();
    const endMs = endDate?.getTime();
    if (startMs && endMs) {
      dispatch(setRegularStartEndDate({ startDate: startMs, endDate: endMs }));
    }
  };

  const handleStartDateTimeChange = (date: Dayjs) => {
    dispatch(setHighGrainStartDateTime(date.toDate().getTime()));
    dispatch(setHighGrainEndDateTime(getEndDateTime(date.toDate()).getTime()));
  };

  const handleEndDateTimeChange = (date: Dayjs) => {
    dispatch(setHighGrainEndDateTime(date.toDate().getTime()));
  };

  const handleApplyGrainCall = () => {
    dispatch(setHighGrainExecute(true));
  };

  return (
    <>
      {activeButton === 'Custom' && (
        <>
          {dataType === 'regular' && (
            <CustomDateRangePicker
              startDate={memoisedStartDate}
              endDate={memoisedEndDate}
              autoOpenEnabled={false}
              maxDate={MAX_DATE}
              sx={{
                minWidth: '148px',
                maxWidth: '148px',
                width: '148px',
              }}
              onFinalDateChange={handleFinalRegularDateChange}
            />
          )}
        </>
      )}
      {dataType === 'highGrain' && (
        <>
          <DateSelectorMui
            startDateTimeUTC={startDateTimeDayjs}
            endDateTimeUTC={endDateTimeDayjs}
            handleStartDateTimeChange={handleStartDateTimeChange}
            handleEndDateTimeChange={handleEndDateTimeChange}
            openEndDate={openEndDate}
            setOpenEndDate={setOpenEndDate}
            format={DATE_TIME_FORMAT}
            startMaxDate={startMaxDayjs}
            startMinDate={startMinDayjs}
            endMinDateTime={startDateTimeDayjs}
            endMaxDateTime={maxEndTimeDayjs}
          />
          <StyledButton onClick={handleApplyGrainCall}>
            {t('apply')}
          </StyledButton>
          <StyledTypography variant="small" display="block">
            {t('only_4_hours_time_window_can_be_selected')}
          </StyledTypography>
        </>
      )}
    </>
  );
};

SensorAnalysisCustomDateComponent.displayName =
  'SensorAnalysisCustomDateComponent';

export default SensorAnalysisCustomDateComponent;
